import { z } from "zod";

export const Vector3Schema = z.object({ x: z.number(), y: z.number(), z: z.number() });
export type Vector3Value = z.infer<typeof Vector3Schema>;

export const MoneySchema = z.object({ copper: z.number().int().nonnegative() });
export type Money = z.infer<typeof MoneySchema>;

export const ItemCategorySchema = z.enum([
  "house",
  "furniture",
  "exterior",
  "plant",
  "pet",
  "room",
  "rare"
]);
export type ItemCategory = z.infer<typeof ItemCategorySchema>;

export const RaritySchema = z.enum(["common", "uncommon", "rare", "event"]);
export type Rarity = z.infer<typeof RaritySchema>;

export const CatalogItemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  category: ItemCategorySchema,
  rarity: RaritySchema,
  priceCopper: z.number().int().nonnegative(),
  /** @deprecated Read by nothing. Fit is decided by footprint and category now. */
  socketTypes: z.array(z.string()).optional(),
  variants: z.record(z.string(), z.array(z.string())),
  giftable: z.boolean(),
  stackable: z.boolean().default(false),
  achievement: z.string().optional()
});
export type CatalogItem = z.infer<typeof CatalogItemSchema>;

/**
 * @deprecated Free-form placement replaced fixed slots. Kept only so a world persisted with
 * sockets still parses and `upgradePlacements` can read the old positions out of it.
 */
export const PlacementSocketSchema = z.object({
  id: z.string(),
  type: z.string(),
  position: Vector3Schema,
  rotationY: z.number(),
  allowedCategories: z.array(ItemCategorySchema),
  allowedRotations: z.array(z.number())
});
export type PlacementSocket = z.infer<typeof PlacementSocketSchema>;

export const RoomIdSchema = z.enum(["living", "kitchen", "bedroom", "hobby"]);
export type RoomId = z.infer<typeof RoomIdSchema>;

/** Which side of the walls a placement lives on. Interior is not open yet — see placement-rules. */
export const PlacementSurfaceSchema = z.enum(["exterior", "interior"]);
export type PlacementSurface = z.infer<typeof PlacementSurfaceSchema>;

/**
 * The sizes an item can be placed at, smallest first. The default is the largest: an item arrives
 * at the size it was drawn and you shrink it from there, never the other way.
 *
 * Placements store the *index*, not the value. A slider emits integers cleanly where a float
 * round-trips as 0.7500000001 and fails a literal check, and a later fifth size widens a range
 * instead of rewriting everybody's saved data.
 */
export const PLACEMENT_SCALE_STEPS = [0.25, 0.5, 0.75, 1] as const;
export const PLACEMENT_SCALE_MAX_STEP = PLACEMENT_SCALE_STEPS.length - 1;

/**
 * How much smaller the item art is than it was drawn.
 *
 * The catalogue models were authored to fill a viewer, not to stand in a garden: the acorn
 * mailbox is nine voxels tall against a hobbit hole's eight, and the picnic blanket is as wide as
 * a cottage. They are all oversized by about the same factor, so one correction fixes the lot.
 *
 * This is a property of the art, not a choice the member makes. Full size on the slider means
 * this, and shrinking works down from here.
 */
export const ITEM_ART_SCALE = 0.25;

/** What the member sees on the slider: 1 is full size, 0.25 is the smallest. */
export function placementSizeFraction(step: number): number {
  return PLACEMENT_SCALE_STEPS[step] ?? 1;
}

/** What the renderer and the boundary rule use. Both must agree, so both call this. */
export function placementScale(step: number): number {
  return placementSizeFraction(step) * ITEM_ART_SCALE;
}

/** Quarter turns clockwise about Y. Integers keep the boundary maths exact. */
export function placementRotationY(quarters: number): number {
  return (quarters & 3) * (Math.PI / 2);
}

/** How many things one property may put on show. */
export const PLACEMENT_LIMIT = 48;

export const ItemPlacementSchema = z
  .object({
    id: z.string().min(1),
    itemId: z.string().min(1),
    surface: PlacementSurfaceSchema.default("exterior"),
    /** Required when the surface is interior, absent otherwise. */
    room: RoomIdSchema.optional(),
    /** Plot-local voxels outdoors, room-local voxels indoors. */
    position: z.object({
      x: z.number().finite(),
      y: z.number().finite(),
      z: z.number().finite()
    }),
    rotationQuarters: z.number().int().min(0).max(3).default(0),
    scaleStep: z.number().int().min(0).max(PLACEMENT_SCALE_MAX_STEP).default(PLACEMENT_SCALE_MAX_STEP),
    selectedVariants: z.record(z.string(), z.string())
  })
  .superRefine((placement, context) => {
    if (placement.surface === "interior" && !placement.room) {
      context.addIssue({ code: "custom", path: ["room"], message: "An interior placement needs a room" });
    }
    if (placement.surface === "exterior" && placement.room) {
      context.addIssue({ code: "custom", path: ["room"], message: "An exterior placement has no room" });
    }
  });
export type ItemPlacement = z.infer<typeof ItemPlacementSchema>;

export const PetSpeciesSchema = z.enum(["cat", "dog", "rabbit", "chicken", "turtle", "sheep"]);
export type PetSpecies = z.infer<typeof PetSpeciesSchema>;

export const PetStateSchema = z.object({
  id: z.string(),
  species: PetSpeciesSchema,
  name: z.string().min(1).max(24),
  appearance: z.string(),
  affection: z.number().int().min(0).max(100),
  slot: z.number().int().min(0).max(2),
  unlockedAnimations: z.array(z.string())
});
export type PetState = z.infer<typeof PetStateSchema>;

export const PlantStateSchema = z.object({
  id: z.string(),
  itemId: z.string(),
  bedIndex: z.number().int().min(0).max(3),
  growth: z.number().int().min(0).max(4),
  plantedAt: z.string()
});
export type PlantState = z.infer<typeof PlantStateSchema>;

/** The home silhouettes the voxel generator can actually build for a member. */
export const HomeTypeSchema = z.enum(["hobbit_hole", "tudor_cottage", "cozy_cabin"]);
export type HomeType = z.infer<typeof HomeTypeSchema>;

export const PropertyStateSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  ownerName: z.string(),
  name: z.string().min(1).max(32),
  plotId: z.string(),
  /**
   * Null until the member picks, which they earn by finishing their first quest. A parcel with
   * no home type on it is empty ground — the home appears the moment it is chosen.
   */
  homeType: HomeTypeSchema.nullable(),
  /**
   * How far the parcel reaches, as an index into `PLOT_RADII`. Everyone starts on the smallest
   * and buys their way out; the biggest yard items have nowhere to stand until they do.
   */
  plotTier: z.number().int().min(0).max(2).default(0),
  unlockedRooms: z.array(RoomIdSchema),
  placements: z.array(ItemPlacementSchema).max(PLACEMENT_LIMIT),
  pets: z.array(PetStateSchema).max(3),
  plants: z.array(PlantStateSchema).max(4),
  version: z.number().int().positive()
});
export type PropertyState = z.infer<typeof PropertyStateSchema>;

export const QuestKindSchema = z.enum(["main", "bonus", "helpful", "lfg", "event"]);
export type QuestKind = z.infer<typeof QuestKindSchema>;

export const VerificationTypeSchema = z.enum([
  "reply",
  "message",
  "attachment",
  "peer",
  "voice_confirmed"
]);
export type VerificationType = z.infer<typeof VerificationTypeSchema>;

export const QuestTemplateSchema = z.object({
  id: z.string(),
  prompt: z.string(),
  channelGroup: z.string(),
  kind: QuestKindSchema,
  rewardCopper: z.number().int().nonnegative(),
  projectEffort: z.number().int().nonnegative(),
  verification: VerificationTypeSchema,
  enabled: z.boolean()
});
export type QuestTemplate = z.infer<typeof QuestTemplateSchema>;

export const ProjectStageSchema = z.object({
  threshold: z.number().min(0).max(1),
  name: z.string(),
  description: z.string()
});

export const CommunityProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  goal: z.number().int().positive(),
  progress: z.number().int().nonnegative(),
  status: z.enum(["active", "queued", "complete"]),
  queuePosition: z.number().int().nonnegative(),
  stages: z.array(ProjectStageSchema),
  contributors: z.number().int().nonnegative()
});
export type CommunityProject = z.infer<typeof CommunityProjectSchema>;

export const PlotSchema = z.object({
  id: z.string(),
  neighborhoodId: z.string(),
  template: z.enum(["grove", "bend", "creekside", "wedge", "hill", "meadow"]),
  position: Vector3Schema,
  rotationY: z.number(),
  elevation: z.number(),
  assignedOwnerId: z.string().nullable(),
  /** @deprecated See `PlacementSocketSchema`. Optional so a socket-free world still parses. */
  sockets: z.array(PlacementSocketSchema).optional().default([])
});
export type Plot = z.infer<typeof PlotSchema>;

export const PathSchema = z.object({
  id: z.string(),
  points: z.array(Vector3Schema)
});
export type VillagePath = z.infer<typeof PathSchema>;

export const NeighborhoodSchema = z.object({
  id: z.string(),
  center: Vector3Schema,
  plotIds: z.array(z.string())
});
export type Neighborhood = z.infer<typeof NeighborhoodSchema>;

export const WorldStateSchema = z.object({
  seed: z.string(),
  neighborhoods: z.array(NeighborhoodSchema),
  plots: z.array(PlotSchema),
  paths: z.array(PathSchema),
  /**
   * Parcel boundaries between neighbouring plots — the hedges and fences that divide the
   * hamlet into irregular fields. Optional so a world persisted before they existed still
   * parses; the generator version bump regenerates it with them.
   */
  hedges: z.array(PathSchema).optional(),
  /**
   * How many times the village has grown past its original seven neighborhoods.
   *
   * Zero, and absent, mean the same thing — a world persisted before the village could expand.
   * Each generation is a satellite hamlet placed outside everything already standing, so the
   * number is both the count of rings and the seed input that makes the next one reproducible.
   */
  generation: z.number().int().nonnegative().optional().default(0),
  generatedAt: z.string()
});
export type WorldState = z.infer<typeof WorldStateSchema>;

export const WalletTransactionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  amountCopper: z.number().int(),
  balanceAfter: z.number().int().nonnegative(),
  reason: z.string(),
  idempotencyKey: z.string(),
  createdAt: z.string()
});
export type WalletTransaction = z.infer<typeof WalletTransactionSchema>;

export const GiftSchema = z.object({
  id: z.string(),
  senderId: z.string(),
  recipientId: z.string(),
  itemId: z.string(),
  status: z.enum(["delivered", "rejected", "moderation_hold"]),
  createdAt: z.string()
});
export type Gift = z.infer<typeof GiftSchema>;

/**
 * Another member's parcel, as seen from the street.
 *
 * Only what is visible from outside: which home stands there, how far the fence reaches, and what
 * is out in the garden. Nothing about their wallet, quests or the inside of the house.
 */
export const NeighbourHomeSchema = z.object({
  memberId: z.string(),
  /**
   * What to call them. Defaulted rather than required, so a snapshot written before names were
   * carried still parses — and so the one member whose row is missing a name shows as a neighbour
   * rather than costing everyone else their village.
   */
  displayName: z.string().default("Tribe member"),
  plotId: z.string(),
  homeType: HomeTypeSchema.nullable(),
  plotTier: z.number().int().min(0).max(2).default(0),
  placements: z.array(ItemPlacementSchema).max(PLACEMENT_LIMIT)
});
export type NeighbourHome = z.infer<typeof NeighbourHomeSchema>;

export interface ActivityBootstrap {
  guild: { id: string; name: string; isModerator: boolean };
  member: { id: string; displayName: string; avatarUrl?: string };
  wallet: Money;
  property: PropertyState;
  /** Everyone else's parcel, so the village shows the homes people actually chose. */
  neighbours: NeighbourHome[];
  world: WorldState;
  catalog: CatalogItem[];
  ownedItemIds: string[];
  activeQuests: QuestTemplate[];
  project: CommunityProject;
  gifts: Gift[];
  rewardInbox: Array<{ id: string; label: string; copper: number; projectEffort: number; createdAt: string }>;
}
